export const JOHKU_EMBED_ALIGNS = ['left', 'right', 'center'] as const
export type JohkuEmbedAlign = (typeof JOHKU_EMBED_ALIGNS)[number]

export const JOHKU_EMBED_SIZES = ['s', 'm', 'l'] as const
export type JohkuEmbedSize = (typeof JOHKU_EMBED_SIZES)[number]

export function johkuEmbedAlign(align?: string | null): JohkuEmbedAlign {
  return JOHKU_EMBED_ALIGNS.includes(align as JohkuEmbedAlign) ? (align as JohkuEmbedAlign) : 'left'
}

export function johkuEmbedSize(size?: string | null): JohkuEmbedSize {
  return JOHKU_EMBED_SIZES.includes(size as JohkuEmbedSize) ? (size as JohkuEmbedSize) : 'm'
}

export function johkuEmbedClass(align?: string | null, size?: string | null): string {
  return `johku-embed-image johku-embed-image--${johkuEmbedAlign(align)} johku-embed-image--${johkuEmbedSize(size)}`
}

export function johkuEmbedDisplaySrc(imageBaseUrl: string, fileId: string | number, width = 800): string {
  const id = String(fileId).replace(/[^\d]/g, '')
  if (!imageBaseUrl || !id) {
    return ''
  }
  const base = imageBaseUrl.replace(/\/$/, '')
  let originAndShop = base
  try {
    const url = new URL(base.includes('://') ? base : `https://${base}`)
    if (url.hostname === 'johku.com' || url.hostname === 'www.johku.com' || url.hostname === 'kube.johku.com') {
      originAndShop = 'https://cdn.johku.com' + url.pathname.replace(/\/$/, '')
    }
  } catch {
    // keep originAndShop
  }
  return `${originAndShop}/files/${id}/w${width}-h0-s/webp/image.webp`
}

/**
 * Product file image at the campaign popup width, via the image CDN.
 * Hosts the CDN helper does not rewrite keep their original file URL.
 * Campaign images are not files, so this leaves those URLs unchanged.
 */
export function johkuScaledImageSrc (imageUrl: string, width = 800): string {
  if (!imageUrl || imageUrl.startsWith('blob:') || imageUrl.startsWith('data:')) {
    return imageUrl || ''
  }
  const match = imageUrl.match(/^(.*?)\/files\/(\d+)(?:\.[a-z0-9]+)?(?:\?[^#]*)?$/i)
  if (!match) {
    return imageUrl
  }
  const scaled = johkuEmbedDisplaySrc(match[1], match[2], width)
  try {
    const sourceHost = new URL(match[1].includes('://') ? match[1] : `https://${match[1]}`).hostname
    const scaledHost = new URL(scaled).hostname
    if (scaledHost !== sourceHost) {
      return scaled
    }
  } catch {
    return imageUrl
  }
  return imageUrl
}

export function embeddedFileIds(html?: string | null): Set<string> {
  const ids = new Set<string>()
  if (!html) {
    return ids
  }
  const re = /data-johku-file-id=["']?(\d+)/gi
  let match
  while ((match = re.exec(html)) !== null) {
    ids.add(match[1])
  }
  return ids
}

export function stripEmbeddedImage(html: string | null | undefined, fileId: string | number): string {
  if (!html) {
    return html || ''
  }
  const id = String(fileId).replace(/[^\d]/g, '')
  if (!id) {
    return html
  }
  return html.replace(new RegExp(`<img\\b[^>]*data-johku-file-id=["']?${id}["']?[^>]*(?:\\/?>|>)`, 'gi'), '')
}
